import _ from "lodash";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Icon, Input } from "semantic-ui-react";
import Histogram from "../../../../Lumber/Histogram";
import { makeColorStopsValid, type ColorStop } from "../MapStyles";
import { useData, useViewerDispatch } from "../StateManagement";
import { DraggableWindow } from "./InternalComponents/DraggableWindow";
import { GradientButton } from "./InternalComponents/GradientButton";
import { TransparencySlider } from "./InternalComponents/TransparencySlider";

export const HeatmapOptions = () => {
	const [popupOpen, setPopupOpen] = useState(false);

	return (
		<>
			<Button className="actionsPopupTrigger iconButton" icon onClick={() => setPopupOpen((prev) => !prev)}>
				<Tooltip
					placement="top"
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
					trigger="hover"
					motion={{ motionName: "rc-tooltip-zoom" }}
					overlay="Options"
				>
					<Icon className="cog" size="large" style={{ width: 21, height: 21 }} />
				</Tooltip>
			</Button>
			{createPortal(<HeatmapOptionsPopup open={popupOpen} setOpen={setPopupOpen} />, document.body)}
		</>
	);
};

type PopupProps = {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const HeatmapOptionsPopup = ({ open, setOpen }: PopupProps) => {
	const NUM_BINS = 50;
	const INIT_AUTO_THRESH = 200;
	const GRADIENTS: { [key: string]: ColorStop[] } = useMemo(
		() => ({
			ryg: [
				{ color: "#ff0000", stop: 0 },
				{ color: "#ffff00", stop: 0.5 },
				{ color: "#00ff00", stop: 1 },
			],
			mono: [
				{ color: "#000000", stop: 0 },
				{ color: "#20b7f1", stop: 1 },
			],
		}),
		[],
	);

	const { updateHeatmapDomain, updateHeatmapOpacity, updateHeatmapColorStops } = useViewerDispatch();
	const { heatmapData, plotData, hideExclusions } = useData();

	const [heatmapDomain, setHeatmapDomain] = useState<[number, number]>([0, 1]);
	const [heatmapOpacity, setHeatmapOpacity] = useState(0.65);

	const [selectedGradient, setSelectedGradient] = useState("ryg");
	const [autoThresh, setAutoThresh] = useState(INIT_AUTO_THRESH);
	const [minInput, setMinInput] = useState(0);
	const [maxInput, setMaxInput] = useState(0);
	const [minInputFocused, setMinInputFocused] = useState(false);
	const [maxInputFocused, setMaxInputFocused] = useState(false);
	const [minInputErr, setMinInputErr] = useState(false);
	const [maxInputErr, setMaxInputErr] = useState(false);

	const heatmapValues: number[] = useMemo(() => {
		if (heatmapData) {
			return _.reduce(
				heatmapData,
				(acc, curr) => {
					const plot = plotData?.find((pd) => pd.plotId === curr.plotId);
					if (!hideExclusions || !plot?.status?.includes("Excluded")) acc.push(curr.plotAnalysisResultValue);
					return acc;
				},
				[] as number[],
			);
		}
		return [];
	}, [heatmapData, hideExclusions, plotData]);

	const [prevHeatmapValues, setPrevHeatmapValues] = useState<number[]>(heatmapValues);

	if (prevHeatmapValues !== heatmapValues) {
		setPrevHeatmapValues(heatmapValues);
		if (heatmapValues.length > 0) {
			const temp: [number, number] = [Math.min(...heatmapValues), Math.max(...heatmapValues)];
			setHeatmapDomain(temp);
			setMinInput(temp[0]);
			setMaxInput(temp[1]);
		}
	}

	useEffect(() => {
		updateHeatmapDomain(heatmapDomain);
	}, [heatmapDomain, updateHeatmapDomain]);

	// useEffect(() => {}, [selectedGradient]);

	const bins: { [key: number]: number } = useMemo(() => {
		const arr: { [key: number]: number } = {};
		const min = Math.min(...heatmapValues);
		const max = Math.max(...heatmapValues);
		const interval = (max - min) / (NUM_BINS - 1);

		for (let i = 0; i < NUM_BINS; i++) {
			const binRange = [min + interval * i, min + interval * (i + 1)];
			arr[binRange[0]] = _.reduce(
				heatmapValues,
				(acc, curr) => {
					if (curr >= binRange[0] && (curr < binRange[1] || binRange[0] === curr)) {
						return acc + 1;
					} else {
						return acc;
					}
				},
				0,
			);
		}

		return arr;
	}, [heatmapValues]);

	useEffect(() => {
		if (heatmapValues.length > 0) {
			const includedHeatmapValues = _.filter(
				heatmapValues,
				(hv) => hv !== null && hv >= heatmapDomain[0] && hv <= heatmapDomain[1],
			);
			const sortedHeatmapValues = includedHeatmapValues.sort((a, b) => a - b);

			const nAtPercent = (percent: number) => {
				const numsLen = sortedHeatmapValues.length - 1;
				const floorHit = sortedHeatmapValues[Math.floor(percent * numsLen)];
				const ceilHit = sortedHeatmapValues[Math.ceil(percent * numsLen)];
				const target = (floorHit + ceilHit) / 2;
				return target;
			};

			const gradient = GRADIENTS[selectedGradient];

			const stops = gradient.map(({ color, stop }) => ({ color, stop: nAtPercent(stop) }));
			const validated = makeColorStopsValid(stops);
			updateHeatmapColorStops(validated);
		}
	}, [heatmapValues, selectedGradient, heatmapDomain, plotData, GRADIENTS, updateHeatmapColorStops]);

	//TASK: (i made this task for searchability for the tasks below)
	/*
			TASKS:
			The functionality should be all complete with this component
			
			1. Add Labels to each slider
		*/

	const autoAdjust = useCallback(() => {
		const binRanges = Object.keys(bins);
		const binCounts = Object.values(bins);

		let autoMin = 0;
		let autoMax = 0;
		const size = _.reduce(binCounts, (sum, a) => sum + a, 0);
		setAutoThresh(autoThresh < 10 ? INIT_AUTO_THRESH : autoThresh / 2);
		const lim = size / 10;
		const thresh = size / autoThresh;

		let count = 0;
		let i = 0;
		while (i < NUM_BINS - 1 && count <= thresh) {
			i++;
			count = binCounts[i];
			if (count > lim) {
				count = 0;
			}
		}
		autoMin = i;

		count = 0;
		i = NUM_BINS - 1;
		while (i > 0 && count <= thresh) {
			i--;
			count = binCounts[i];
			if (count > lim) {
				count = 0;
			}
		}
		autoMax = i;

		if (autoMax < autoMin) {
			autoMin = 0;
			autoMax = NUM_BINS - 1;
			setAutoThresh(INIT_AUTO_THRESH);
		}

		return [Number(binRanges[autoMin]), Number(binRanges[autoMax])] as [number, number];
	}, [bins, autoThresh]);

	const resetAdjust = () => {
		setAutoThresh(INIT_AUTO_THRESH);
		const temp: [number, number] = [Math.min(...heatmapValues), Math.max(...heatmapValues)];
		setHeatmapDomain(temp);
		setMinInput(temp[0]);
		setMaxInput(temp[1]);
		setHeatmapOpacity(0.65);
	};

	return open ? (
		<DraggableWindow
			zId="heatmap"
			title="Heatmap Options"
			open={open}
			setOpen={setOpen}
			rootStyle={{ width: 400 }}
			contentClassName="plotOptionsModal"
		>
			<Histogram
				buckets={bins}
				min={Math.min(...heatmapValues)}
				max={Math.max(...heatmapValues)}
				slider={Math.min(...heatmapValues) !== Math.max(...heatmapValues)}
				sliderValues={heatmapDomain}
				count={Object.values(bins).length}
				// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
				onSliderUpdate={(v: [number, number]) => {
					setHeatmapDomain(v);
					if (!minInputFocused) {
						setMinInput(v[0]);
					}
					if (!maxInputFocused) {
						setMaxInput(v[1]);
					}
				}}
				rootStyle={{ height: 150, width: "auto", gridArea: "hist" }}
				colorscale={GRADIENTS[selectedGradient].map(({ stop, color }) => [stop, color])}
			/>

			<TransparencySlider
				rootStyle={{ gridArea: "opacity" }}
				currentOpacity={heatmapOpacity}
				onChange={(opacity) => {
					const rounded = Math.round(opacity * 100) / 100;
					setHeatmapOpacity(rounded);
					updateHeatmapOpacity(rounded);
				}}
			/>

			<Input
				size="mini"
				style={{ width: 75, gridArea: "min" }}
				type="number"
				step={0.01}
				value={_.floor(minInput, 2)}
				error={minInputErr}
				onFocus={() => {
					setMinInputFocused(true);
				}}
				onBlur={() => {
					setMinInputFocused(false);
				}}
				onChange={(e) => {
					setMinInput(Number(e.target.value));
					let v: number = NaN;
					if (e.target.value) {
						v = Number(e.target.value);
						if (isNaN(v) || v < Math.min(...heatmapValues) || v > Math.max(...heatmapValues)) {
							setMinInputErr(true);
						} else {
							setMinInputErr(false);
							if (v === heatmapDomain[0]) return;
							setHeatmapDomain((prev) => [v, prev[1]]);
						}
					}
				}}
			/>

			<Input
				size="mini"
				style={{ width: 75, gridArea: "max" }}
				type="number"
				step={0.01}
				value={_.ceil(maxInput, 2)}
				error={maxInputErr}
				onFocus={() => {
					setMaxInputFocused(true);
				}}
				onBlur={() => {
					setMaxInputFocused(false);
				}}
				onChange={(e) => {
					setMaxInput(Number(e.target.value));
					let v: number = NaN;
					if (e.target.value) {
						v = Number(e.target.value);
						if (isNaN(v) || v < Math.min(...heatmapValues) || v > Math.max(...heatmapValues)) {
							setMaxInputErr(true);
						} else {
							setMaxInputErr(false);
							if (v === heatmapDomain[1]) return;
							setHeatmapDomain((prev) => [prev[0], v]);
						}
					}
				}}
			/>

			<div style={{ display: "inline-flex", alignItems: "center", gridArea: "adjust" }}>
				<Button
					color="blue"
					compact
					floated="left"
					content="Auto"
					disabled={!bins}
					onClick={() => {
						const autoDomain = autoAdjust();
						setHeatmapDomain(autoDomain);
					}}
				/>
				{bins && (
					<Tooltip
						placement="right"
						align={{ offset: [-3, 0] }}
						mouseEnterDelay={0}
						mouseLeaveDelay={0}
						trigger="hover"
						overlay={
							<>
								<p style={{ textAlign: "center" }}>
									Automatically adjusts the heatmap to improve contrast
									<br />
									Repeated presses will further adjust the heatmap
								</p>
							</>
						}
					>
						<Icon circular inverted name="info" size="tiny" link />
					</Tooltip>
				)}
			</div>

			<Button
				style={{ gridArea: "reset" }}
				negative
				compact
				floated="right"
				content="Reset"
				onClick={() => {
					resetAdjust();
				}}
			/>
			<div className="buttonContainer">
				<GradientButton
					selected={selectedGradient === "ryg"}
					onClick={() => {
						setSelectedGradient("ryg");
					}}
					title="Red-Yellow-Green"
					colorStops={GRADIENTS["ryg"]}
				/>
				<GradientButton
					selected={selectedGradient === "mono"}
					onClick={() => {
						setSelectedGradient("mono");
					}}
					title="Monochromatic"
					colorStops={GRADIENTS["mono"]}
				/>
			</div>
		</DraggableWindow>
	) : (
		<></>
	);
};
